import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Plus, X, ExternalLink, Trash2, ArrowUp, ArrowDown, ArrowUpDown, ChevronDown,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ReferenceArea,
} from 'recharts';
import { getAvailability, getAvailabilityOverrides, createAvailabilityOverride, deleteAvailabilityOverride, getLegacyStatusCodes } from '../api';
import { useUrlParam } from '../hooks/useUrlParam';

// ── Constants ─────────────────────────────────────────────────────────────────
const REASON_OPTIONS = [
  { id: 'personal',           label: 'Personal',                          countsAs: 'available' },
  { id: 'international',      label: 'International duty',                countsAs: 'excluded' },
  { id: 'suspended',          label: 'Suspended',                         countsAs: 'unavailable' },
  { id: 'other_available',    label: 'Other (counts as available)',       countsAs: 'available' },
  { id: 'other_unavailable',  label: 'Other (counts as unavailable)',     countsAs: 'unavailable' },
];

const STATUS_COLORS = {
  available:   '#10b981',
  unavailable: '#ef4444',
  excluded:    '#64748b',
};

const REASON_LABELS = {
  injury: 'Injury', illness: 'Illness', personal: 'Personal', international: 'International duty',
  suspended: 'Suspended', other_available: 'Other', other_unavailable: 'Other', legacy: 'Legacy', none: '—',
};

// ── Date helpers (string-based: local calendar "today", UTC arithmetic) ──
// Local-midnight Dates read back through toISOString() land on the previous day in CET/CEST.
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function presetRange(preset, customFrom, customTo) {
  const today = todayStr();
  if (preset === 'custom') return { from: customFrom || today, to: customTo || today };
  const [y, m, d] = today.split('-').map(Number);
  if (preset === 'week') {
    const dow = (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
    return { from: new Date(Date.UTC(y, m - 1, d - dow)).toISOString().slice(0, 10), to: today };
  }
  if (preset === 'month') {
    return { from: `${today.slice(0, 7)}-01`, to: today };
  }
  if (preset === 'season') {
    // Cutover is June 15 (matches dateToSeason in SeasonContext.js).
    const startYear = today.slice(5) >= '06-15' ? y : y - 1;
    return { from: `${startYear}-06-15`, to: today };
  }
  return { from: today, to: today };
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

// ── Shared UI ─────────────────────────────────────────────────────────────────
const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px', marginBottom: 10 };
const btn  = (active, color = '#3b82f6') => ({
  padding: '6px 12px', borderRadius: 8, border: `1px solid ${active ? color : 'var(--border)'}`,
  background: active ? color : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
  fontSize: 12, fontWeight: active ? 600 : 400, cursor: 'pointer', fontFamily: 'inherit',
});
const inputStyle = {
  padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)',
  background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13,
  fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
};
const selectStyle = { ...inputStyle };
const labelStyle = { fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' };

function Field({ label, children }) {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><label style={labelStyle}>{label}</label>{children}</div>;
}

function StatusPill({ status }) {
  const color = STATUS_COLORS[status] || '#64748b';
  const label = status === 'available' ? 'Available' : status === 'unavailable' ? 'Unavailable' : 'Excluded';
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: color + '22', color, border: `1px solid ${color}44` }}>
      {label}
    </span>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 16, width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}><X size={18} /></button>
        </div>
        <div style={{ padding: '20px' }}>{children}</div>
      </div>
    </div>
  );
}

// ── Searchable legacy code dropdown ────────────────────────────────────────────
function LegacyCodePicker({ codes, value, onChange }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');

  const selected = codes.find(c => c.code === value);
  const filtered = codes.filter(c => c.code.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-app)', color: value ? 'var(--text-primary)' : 'var(--text-muted)', fontSize: 13, textAlign: 'left', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: 'inherit' }}>
        <span>{selected ? selected.code : 'None selected'}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '110%', left: 0, right: 0, background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 8, zIndex: 50, maxHeight: 260, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
          <div style={{ padding: 8, borderBottom: '1px solid var(--border)' }}>
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Search codes..."
              style={{ ...inputStyle }} />
          </div>
          <div onClick={() => { onChange(null); setOpen(false); setSearch(''); }}
            style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)' }}>
            None
          </div>
          {filtered.map(c => (
            <div key={c.code} onClick={() => { onChange(c.code); setOpen(false); setSearch(''); }}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, background: value === c.code ? '#1e3a5f' : 'transparent', color: value === c.code ? '#93c5fd' : 'var(--text-primary)' }}>
              {c.code} <span style={{ color: 'var(--text-dimmed)', fontSize: 11 }}>({c.counts_as})</span>
            </div>
          ))}
          {filtered.length === 0 && <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-dimmed)' }}>No matches.</div>}
        </div>
      )}
    </div>
  );
}

// ── Override modal ────────────────────────────────────────────────────────────
function OverrideModal({ player, defaultDate, user, onSave, onClose }) {
  const [reason, setReason]     = useState('personal');
  const [dateFrom, setDateFrom] = useState(defaultDate);
  const [dateTo, setDateTo]     = useState(defaultDate);
  const [notes, setNotes]       = useState('');
  const [saving, setSaving]     = useState(false);
  const [legacyOpen, setLegacyOpen]   = useState(false);
  const [legacyCode, setLegacyCode]   = useState(null);
  const [legacyCodes, setLegacyCodes] = useState([]);

  useEffect(() => {
    if (!user?.isAdmin) return;
    getLegacyStatusCodes().then(setLegacyCodes).catch(() => {});
  }, [user?.isAdmin]);

  const selectedLegacy = legacyCodes.find(c => c.code === legacyCode);

  const handleSave = async () => {
    const opt = REASON_OPTIONS.find(r => r.id === reason);
    setSaving(true);
    try {
      await createAvailabilityOverride({
        player_id: player.player_id, date_from: dateFrom, date_to: dateTo,
        reason, counts_as: opt.countsAs, legacy_code: legacyCode || null,
        notes: notes || null, created_by: user?.id,
      });
      onSave();
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <Modal title={`Override availability — ${player.player_name}`} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Field label="Reason">
          <select style={selectStyle} value={reason} onChange={e => setReason(e.target.value)}>
            {REASON_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="From"><input style={inputStyle} type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></Field>
          <Field label="To"><input style={inputStyle} type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} /></Field>
        </div>
        <Field label="Notes">
          <textarea style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} value={notes} onChange={e => setNotes(e.target.value)} />
        </Field>

        {user?.isAdmin && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
            <button type="button" onClick={() => setLegacyOpen(o => !o)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>
              <ChevronDown size={13} style={{ transform: legacyOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
              Legacy status code (admin only)
            </button>
            {legacyOpen && (
              <div style={{ marginTop: 10 }}>
                <LegacyCodePicker codes={legacyCodes} value={legacyCode} onChange={setLegacyCode} />
                {selectedLegacy && (
                  <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
                    This will be recorded as <strong style={{ color: STATUS_COLORS[selectedLegacy.counts_as] }}>{selectedLegacy.counts_as}</strong> based on historical classification.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
        <button style={btn(false)} onClick={onClose}>Cancel</button>
        <button style={btn(true)} onClick={handleSave} disabled={saving || dateTo < dateFrom}>{saving ? 'Saving…' : 'Save override'}</button>
      </div>
    </Modal>
  );
}

// ── Daily Roster tab ───────────────────────────────────────────────────────────
function DailyRosterTab({ user, readOnly, onNavigateToMedical }) {
  const [date, setDate]       = useUrlParam('date', todayStr());
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [overrideFor, setOverrideFor] = useState(null);

  const refresh = useCallback(() => {
    setLoading(true);
    getAvailability(date, date, 'total').then(setData).finally(() => setLoading(false));
  }, [date]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleRemoveOverride = async (overrideId) => {
    await deleteAvailabilityOverride(overrideId);
    refresh();
  };

  const isTeamDay = data && data.dates && data.dates.length > 0;
  const players = data?.players || [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <Field label="Date">
          <input style={{ ...inputStyle, width: 180 }} type="date" value={date} onChange={e => setDate(e.target.value)} />
        </Field>
        {isTeamDay && <span style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 18 }}>MD label: <strong>{data.mdLabels[0]}</strong></span>}
      </div>

      {!loading && !isTeamDay && (
        <div style={{ ...card, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.3)' }}>
          <div style={{ fontSize: 13, color: '#f59e0b', fontWeight: 600 }}>
            This date is outside any active scenario — treated as a holiday/gap period and excluded from calculations.
          </div>
        </div>
      )}

      <div style={{ opacity: !loading && !isTeamDay ? 0.4 : 1, pointerEvents: !loading && !isTeamDay ? 'none' : 'auto' }}>
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Player', 'Status', 'Reason / Detail', 'Source', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: 'var(--text-dimmed)' }}>Loading…</td></tr>
              ) : players.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: 'var(--text-dimmed)' }}>No players found.</td></tr>
              ) : players.map(p => {
                const day = p.days[0] || { status: 'available', reason: 'none', source: 'default', detail: null };
                return (
                  <tr key={p.player_id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 600 }}>{p.player_name}</td>
                    <td style={{ padding: '10px 14px' }}><StatusPill status={day.status} /></td>
                    <td style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>
                      {day.legacyCode || day.detail || REASON_LABELS[day.reason] || ''}
                    </td>
                    <td style={{ padding: '10px 14px', color: 'var(--text-dimmed)', fontSize: 12 }}>
                      {day.source === 'medical' ? 'Auto-Medical' : day.source === 'override' ? 'Manual' : 'Default'}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                      {day.source === 'medical' ? (
                        <button onClick={onNavigateToMedical} style={{ ...btn(false), display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
                          Managed in Medical <ExternalLink size={11} />
                        </button>
                      ) : day.source === 'override' && !readOnly ? (
                        <button onClick={() => handleRemoveOverride(day.overrideId)} style={{ ...btn(false, '#ef4444'), display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <Trash2 size={12} /> Remove
                        </button>
                      ) : day.source === 'default' && !readOnly ? (
                        <button onClick={() => setOverrideFor(p)} style={{ ...btn(false), display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <Plus size={12} /> Override
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {overrideFor && (
        <OverrideModal player={overrideFor} defaultDate={date} user={user} onSave={refresh} onClose={() => setOverrideFor(null)} />
      )}
    </div>
  );
}

// ── Per-player aggregation ────────────────────────────────────────────────────
// includedIdx: indices into player.days to actually tally — excludes rest/off ('Vrij') days
// up front (2026-07-25), same rule availability.js's computeAvailabilityPercent applies on
// the backend for Team Table. Defaults to every index so existing single-day callers (the
// Daily Roster tab, which only ever has one day and doesn't fetch restDays) keep working.
function computePlayerStats(player, includedIdx) {
  let available = 0, personal = 0, injury = 0, illness = 0, other = 0, excluded = 0;
  const idxList = includedIdx || player.days.map((_, i) => i);
  idxList.forEach(i => {
    const d = player.days[i];
    if (!d) return;
    if (d.status === 'excluded') { excluded++; return; }
    if (d.status === 'available') {
      if (d.reason === 'personal') personal++;
      else available++;
      return;
    }
    if (d.reason === 'injury') injury++;
    else if (d.reason === 'illness') illness++;
    else other++;
  });
  const tracked = available + personal + injury + illness + other;
  const unavailableDays = injury + illness + other;
  const pct = (n) => tracked ? Math.round((n / tracked) * 100) : null;
  return {
    player_id: player.player_id, player_name: player.player_name, position: player.position,
    available, personal, injury, illness, other, excluded, tracked, unavailableDays,
    availablePct: pct(available + personal), injuryPct: pct(injury), illnessPct: pct(illness), otherPct: pct(other),
  };
}

function StackedBar({ stats }) {
  const segments = [
    { key: 'available', label: 'Available', value: stats.available, color: '#10b981' },
    { key: 'personal',  label: 'Personal',   value: stats.personal,  color: '#8b5cf6' },
    { key: 'injury',    label: 'Injury',     value: stats.injury,    color: '#ef4444' },
    { key: 'illness',   label: 'Illness',    value: stats.illness,   color: '#f59e0b' },
    { key: 'other',     label: 'Other',      value: stats.other,     color: '#94a3b8' },
    { key: 'excluded',  label: 'Excluded',   value: stats.excluded,  color: '#334155' },
  ];
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  return (
    <div>
      <div style={{ display: 'flex', height: 22, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
        {segments.filter(s => s.value > 0).map(s => (
          <div key={s.key} title={`${s.label}: ${s.value}`} style={{ width: `${(s.value / total) * 100}%`, background: s.color }} />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10 }}>
        {segments.map(s => (
          <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text-muted)' }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color, display: 'inline-block' }} />
            {s.label} ({s.value})
          </div>
        ))}
      </div>
    </div>
  );
}

function PlayerDetailModal({ player, includedIdx, onClose }) {
  const stats = useMemo(() => computePlayerStats(player, includedIdx), [player, includedIdx]);
  return (
    <Modal title={player.player_name} onClose={onClose}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
        {player.position || '—'} · {stats.tracked} days tracked
      </div>
      <StackedBar stats={stats} />
    </Modal>
  );
}

// ── Season Analysis tab ────────────────────────────────────────────────────────
function SortHeader({ label, sortKey, activeKey, dir, onClick }) {
  const active = sortKey === activeKey;
  const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th onClick={() => onClick(sortKey)} style={{ textAlign: 'left', padding: '10px 14px', fontSize: 11, fontWeight: 700, color: active ? 'var(--text-primary)' : 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{label} <Icon size={11} /></span>
    </th>
  );
}

function SeasonAnalysisTab() {
  const [mode, setMode]         = useUrlParam('mode', 'total');
  const [preset, setPreset]     = useUrlParam('range', 'season');
  const [customFrom, setCustomFrom] = useUrlParam('date_from', todayStr());
  const [customTo, setCustomTo]     = useUrlParam('date_to', todayStr());
  const [data, setData]         = useState(null);
  const [todayData, setTodayData] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [sortKey, setSortKey]   = useState('unavailableDays');
  const [sortDir, setSortDir]   = useState('desc');
  const [detailPlayer, setDetailPlayer] = useState(null);

  const range = useMemo(() => presetRange(preset, customFrom, customTo), [preset, customFrom, customTo]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getAvailability(range.from, range.to, mode),
      getAvailability(todayStr(), todayStr(), 'total'),
    ]).then(([res, today]) => {
      setData(res);
      setTodayData(today);
    }).finally(() => setLoading(false));
  }, [range.from, range.to, mode]);

  const players = data?.players || [];
  const dates   = data?.dates || [];
  const restDays = data?.restDays || [];

  // Indices into dates/days that actually count — excludes rest/off ('Vrij') days
  // (2026-07-25), same rule the backend's computeAvailabilityPercent applies for Team Table.
  // Every % on this tab is derived from this same index set, so this page can't drift from
  // that number the way it used to (still counting rest days made this tab read 81% for a
  // player Team Table showed 83% for, over the same season).
  const includedIdx = useMemo(() => dates.map((_, i) => i).filter(i => !restDays[i]), [dates, restDays]);

  const playerStats = useMemo(() => players.map(p => computePlayerStats(p, includedIdx)), [players, includedIdx]);

  const overall = useMemo(() => {
    let available = 0, total = 0, excluded = 0;
    players.forEach(p => includedIdx.forEach(i => {
      const d = p.days[i];
      if (!d) return;
      if (d.status === 'excluded') { excluded++; return; }
      total++;
      if (d.status === 'available') available++;
    }));
    return { pct: total ? Math.round((available / total) * 100) : null, total, excluded };
  }, [players, includedIdx]);

  const trend = useMemo(() => {
    return dates.map((dateStr, di) => {
      if (restDays[di]) return { date: fmtDate(dateStr), pct: null };
      let available = 0, total = 0;
      players.forEach(p => {
        const d = p.days[di];
        if (!d || d.status === 'excluded') return;
        total++;
        if (d.status === 'available') available++;
      });
      return { date: fmtDate(dateStr), pct: total ? Math.round((available / total) * 100) : null };
    });
  }, [dates, players, restDays]);

  const positionBar = useMemo(() => {
    const map = {};
    (todayData?.players || []).forEach(p => {
      const day = p.days[0];
      if (!day || day.status !== 'unavailable') return;
      const pos = p.position || 'Unknown';
      map[pos] = (map[pos] || 0) + 1;
    });
    return Object.entries(map).map(([position, count]) => ({ position, count })).sort((a, b) => b.count - a.count);
  }, [todayData]);

  const sortedPlayers = useMemo(() => {
    const list = [...playerStats];
    list.sort((a, b) => {
      const av = a[sortKey] ?? -1, bv = b[sortKey] ?? -1;
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return list;
  }, [playerStats, sortKey, sortDir]);

  const leaderboard = useMemo(() => [...playerStats].sort((a, b) => b.unavailableDays - a.unavailableDays).filter(p => p.unavailableDays > 0).slice(0, 10), [playerStats]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Field label="Mode">
          <div style={{ display: 'flex', gap: 4 }}>
            <button style={btn(mode === 'total')} onClick={() => setMode('total')}>Total availability</button>
            <button style={btn(mode === 'match')} onClick={() => setMode('match')}>Match availability</button>
          </div>
        </Field>
        <Field label="Range">
          <div style={{ display: 'flex', gap: 4 }}>
            {[['week', 'This week'], ['month', 'This month'], ['season', 'Season'], ['custom', 'Custom']].map(([id, label]) => (
              <button key={id} style={btn(preset === id)} onClick={() => setPreset(id)}>{label}</button>
            ))}
          </div>
        </Field>
        {preset === 'custom' && (
          <>
            <Field label="From"><input style={{ ...inputStyle, width: 150 }} type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} /></Field>
            <Field label="To"><input style={{ ...inputStyle, width: 150 }} type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} /></Field>
          </>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: 'var(--text-dimmed)', padding: 40 }}>Loading…</div>
      ) : (
        <>
          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 20 }}>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Overall % available</div>
              <div style={{ fontSize: 30, fontWeight: 800, color: '#10b981' }}>{overall.pct != null ? `${overall.pct}%` : '—'}</div>
            </div>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Team days counted</div>
              <div style={{ fontSize: 30, fontWeight: 800, color: '#3b82f6' }}>{includedIdx.length}</div>
              {dates.length > includedIdx.length && (
                <div style={{ fontSize: 10, color: 'var(--text-dimmed)', marginTop: 2 }}>{dates.length - includedIdx.length} rest day{dates.length - includedIdx.length !== 1 ? 's' : ''} excluded</div>
              )}
            </div>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Days excluded</div>
              <div style={{ fontSize: 30, fontWeight: 800, color: '#64748b' }}>{overall.excluded}</div>
              <div style={{ fontSize: 10, color: 'var(--text-dimmed)', marginTop: 2 }}>player-days (holiday/international)</div>
            </div>
          </div>

          {/* Trend line */}
          <div style={{ ...card }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Availability trend (daily)</div>
            {trend.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text-dimmed)', padding: 20, textAlign: 'center' }}>No data yet.</div>
            ) : (
              <div style={{ height: 220 }}>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={trend} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="date" interval="preserveStartEnd" tick={{ fill: 'var(--text-muted)', fontSize: 10, fontFamily: 'Inter, sans-serif' }} axisLine={false} tickLine={false} />
                    <YAxis domain={[50, 100]} tick={{ fill: 'var(--text-muted)', fontSize: 10, fontFamily: 'Inter, sans-serif' }} axisLine={false} tickLine={false} tickFormatter={v => `${v}%`} />
                    {/* Background zones behind the line: green 90-100, orange 80-90, red below 80 */}
                    <ReferenceArea y1={90} y2={100} fill="#10b981" fillOpacity={0.12} strokeWidth={0} ifOverflow="visible" />
                    <ReferenceArea y1={80} y2={90} fill="#f59e0b" fillOpacity={0.12} strokeWidth={0} ifOverflow="visible" />
                    <ReferenceArea y1={50} y2={80} fill="#ef4444" fillOpacity={0.12} strokeWidth={0} ifOverflow="visible" />
                    <Tooltip
                      contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                      labelStyle={{ color: 'var(--text-primary)', fontWeight: 600 }}
                      formatter={v => [`${v}%`, 'Available']}
                    />
                    <Line type="monotone" dataKey="pct" stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Position bar */}
          <div style={{ ...card }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Unavailable by position (today)</div>
            {positionBar.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text-dimmed)', padding: 20, textAlign: 'center' }}>Full squad available today.</div>
            ) : (
              <div style={{ height: 200 }}>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={positionBar} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="position" tick={{ fill: 'var(--text-muted)', fontSize: 11, fontFamily: 'Inter, sans-serif' }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fill: 'var(--text-muted)', fontSize: 10, fontFamily: 'Inter, sans-serif' }} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                      labelStyle={{ color: 'var(--text-primary)', fontWeight: 600 }}
                    />
                    <Bar dataKey="count" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={50} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Squad table */}
          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', fontSize: 13, fontWeight: 700, borderBottom: '1px solid var(--border)' }}>Squad breakdown</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <SortHeader label="Player"       sortKey="player_name"     activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                  <SortHeader label="Available %"  sortKey="availablePct"    activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                  <SortHeader label="Injury %"     sortKey="injuryPct"       activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                  <SortHeader label="Illness %"    sortKey="illnessPct"      activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                  <SortHeader label="Other %"      sortKey="otherPct"        activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                  <SortHeader label="Days tracked" sortKey="tracked"         activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                </tr>
              </thead>
              <tbody>
                {sortedPlayers.map(p => (
                  <tr key={p.player_id} onClick={() => setDetailPlayer(players.find(pl => pl.player_id === p.player_id))} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 600 }}>{p.player_name}</td>
                    <td style={{ padding: '10px 14px', color: '#10b981' }}>{p.availablePct != null ? `${p.availablePct}%` : '—'}</td>
                    <td style={{ padding: '10px 14px', color: '#ef4444' }}>{p.injuryPct != null ? `${p.injuryPct}%` : '—'}</td>
                    <td style={{ padding: '10px 14px', color: '#f59e0b' }}>{p.illnessPct != null ? `${p.illnessPct}%` : '—'}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{p.otherPct != null ? `${p.otherPct}%` : '—'}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--text-dimmed)' }}>{p.tracked}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Leaderboard */}
          <div style={{ ...card }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Days unavailable — leaderboard</div>
            {leaderboard.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text-dimmed)', padding: 10 }}>No unavailable days in this range.</div>
            ) : leaderboard.map((p, i) => (
              <div key={p.player_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderTop: i > 0 ? '1px solid var(--border)' : 'none' }}>
                <span style={{ width: 20, fontSize: 12, color: 'var(--text-dimmed)' }}>{i + 1}</span>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{p.player_name}</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  inj {p.injury} · ill {p.illness} · other {p.other}
                </span>
                <span style={{ fontSize: 14, fontWeight: 800, color: '#ef4444' }}>{p.unavailableDays}d</span>
              </div>
            ))}
          </div>
        </>
      )}

      {detailPlayer && <PlayerDetailModal player={detailPlayer} includedIdx={includedIdx} onClose={() => setDetailPlayer(null)} />}
    </div>
  );
}

// ── Root export ───────────────────────────────────────────────────────────────
export default function Availability({ user, onNavigateToMedical, initialTab = 'roster' }) {
  // URL wins over the initialTab prop when both are present — matches how playerOverride
  // defaults are handled on the GPS pages (URL is the more specific, more recent intent).
  const [tab, setTab] = useUrlParam('availability_tab', initialTab);
  const readOnly = !(user?.isAdmin || user?.role === 'physio');

  return (
    <div style={{ maxWidth: 980, margin: '0 auto' }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 12 }}>
        <button style={btn(tab === 'roster')} onClick={() => setTab('roster')}>Daily Roster</button>
        <button style={btn(tab === 'analysis')} onClick={() => setTab('analysis')}>Season Analysis</button>
      </div>

      {tab === 'roster'   && <DailyRosterTab user={user} readOnly={readOnly} onNavigateToMedical={onNavigateToMedical} />}
      {tab === 'analysis' && <SeasonAnalysisTab />}
    </div>
  );
}
